import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DemoModal from 'components/DemoModal';
import '../../styles/TailRiskGlobal.css';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5050/api';

const cardStyle = {
  position: 'relative',
  width: '100%',
  maxWidth: '480px',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '6px',
  padding: '2.5rem 2.5rem 2rem',
  backdropFilter: 'blur(8px)',
};

const fieldLabelStyle = {
  display: 'block',
  fontFamily: 'var(--font-cond)',
  fontSize: '0.65rem',
  fontWeight: 600,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.55)',
  marginBottom: '0.45rem',
};

const inputBaseStyle = {
  width: '100%',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: '4px',
  color: '#ffffff',
  fontFamily: 'var(--font-body)',
  fontSize: '0.9rem',
  padding: '0.72rem 0.9rem',
  outline: 'none',
  boxSizing: 'border-box',
};

const btnSubmitStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  background: 'var(--gold)',
  color: '#0a1118',
  fontFamily: 'var(--font-cond)',
  fontWeight: 700,
  fontSize: '0.8rem',
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  padding: '0.85rem 1.5rem',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  marginTop: '0.4rem',
};

const AUM_OPTIONS = [
  'Under $100M',
  '$100M – $250M',
  '$250M – $750M',
  '$750M – $2B',
  '$2B – $5B',
  'Over $5B',
];

const INTEREST_OPTIONS = [
  'Core Platform — Market Value Analytics',
  'Periodic Risk Reports',
  'Continuous Risk Monitoring',
  'Illiquid Asset Risk Methodology',
  'Full Platform Overview',
];

function TextInput({ id, label, type = 'text', value, onChange, placeholder, autoComplete, required = false }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label htmlFor={id} style={fieldLabelStyle}>
        {label}{required && <span style={{ color: '#C9A84C', marginLeft: '0.2rem' }}>*</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        style={{
          ...inputBaseStyle,
          background: focused ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.04)',
          borderColor: focused ? 'rgba(201,168,76,0.55)' : 'rgba(255,255,255,0.14)',
        }}
      />
    </div>
  );
}

function SelectInput({ id, label, value, onChange, options, placeholder }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label htmlFor={id} style={fieldLabelStyle}>{label}</label>
      <select
        id={id}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          ...inputBaseStyle,
          background: focused ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.04)',
          borderColor: focused ? 'rgba(201,168,76,0.55)' : 'rgba(255,255,255,0.14)',
          appearance: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='rgba(255,255,255,0.4)' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 0.9rem center',
          paddingRight: '2.2rem',
          cursor: 'pointer',
        }}
      >
        <option value="" disabled style={{ background: '#0f1923' }}>{placeholder}</option>
        {options.map(opt => (
          <option key={opt} value={opt} style={{ background: '#0f1923' }}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

export default function CreateAccount() {
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [aum, setAum] = useState('');
  const [primaryInterest, setPrimaryInterest] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const [demoOpen, setDemoOpen] = useState(false);

  // Prevent body scroll when demo modal is open
  useEffect(() => {
    document.body.style.overflow = demoOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [demoOpen]);

  const clearError = () => setError('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName || !lastName || !email || !companyName) {
      setError('Please fill in all required fields.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await axios.post(`${apiUrl}/register`, {
        firstName, lastName, email, companyName, phone, address, aum, primaryInterest,
      });
      setDone(true);
    } catch (err) {
      if (err.response) {
        setError(err.response.data.error || 'Registration failed. Please try again.');
      } else if (err.request) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Create Account — Tail Risk Global</title>
        <meta name="description" content="Create your Tail Risk Global client portal account." />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Barlow:wght@300;400;500;600&family=Barlow+Condensed:wght@400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>

      <div className="trg-page" style={{ minHeight: '100vh' }}>

        {/* Nav */}
        <nav>
          <div className="nav-inner">
            <a href="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
              <svg width="26" height="19" viewBox="0 0 32 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.45 3.78L24.71 9.04L26.6 7.15L19.45 0L14.81 4.64L28.04 17.87L27.16 18.75L13.96 5.54L0 19.5L1.89 21.39L13.96 9.33L27.16 22.53L31.82 17.87L18.6 4.64L19.45 3.78Z" fill="#C9A84C" />
              </svg>
              <span style={{ fontFamily: 'var(--font-cond)', fontSize: '0.9rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#fff' }}>
                Tail Risk <span style={{ color: '#d4b06a' }}>Global</span>
              </span>
            </a>
            <a
              href="/"
              style={{ fontFamily: 'var(--font-cond)', fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
            >
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M10 7H2M5 3L1 7l4 4" /></svg>
              Back to home
            </a>
          </div>
        </nav>

        {/* Page */}
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '100px 1.5rem 3rem', position: 'relative', overflow: 'hidden' }}>
          {/* Ambient glow */}
          <div style={{ position: 'absolute', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(201,168,76,0.05) 0%, transparent 70%)', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }} />

          <div style={cardStyle}>
            {/* Gold top accent */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)', opacity: 0.5, borderRadius: '6px 6px 0 0' }} />

            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '1.75rem' }}>
              <svg width="20" height="14" viewBox="0 0 32 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.45 3.78L24.71 9.04L26.6 7.15L19.45 0L14.81 4.64L28.04 17.87L27.16 18.75L13.96 5.54L0 19.5L1.89 21.39L13.96 9.33L27.16 22.53L31.82 17.87L18.6 4.64L19.45 3.78Z" fill="#C9A84C" />
              </svg>
              <div>
                <div style={{ fontFamily: 'var(--font-cond)', fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#fff' }}>
                  Tail Risk <span style={{ color: '#d4b06a' }}>Global</span>
                </div>
                <div style={{ fontFamily: 'var(--font-cond)', fontSize: '0.62rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#7a8ea6', marginTop: '2px' }}>
                  Client Portal
                </div>
              </div>
            </div>

            {!done ? (
              <>
                {/* Gold divider */}
                <div style={{ width: '28px', height: '2px', background: '#C9A84C', opacity: 0.5, marginBottom: '1.2rem' }} />

                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.65rem', fontWeight: 600, lineHeight: 1.2, color: '#fff', marginBottom: '0.5rem' }}>
                  Create your<br />account
                </h1>
                <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, marginBottom: '1.75rem' }}>
                  Submit your details and we'll review your application within one business day.
                </p>

                {error && (
                  <div style={{ background: 'rgba(220,50,50,0.12)', border: '1px solid rgba(220,50,50,0.3)', color: '#ff8a8a', fontFamily: 'var(--font-cond)', fontSize: '0.78rem', letterSpacing: '0.04em', padding: '0.6rem 0.85rem', marginBottom: '1rem', borderRadius: '3px' }}>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate>
                  {/* First / Last name row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                    <TextInput
                      id="ca-first"
                      label="First Name"
                      value={firstName}
                      onChange={(e) => { setFirstName(e.target.value); clearError(); }}
                      placeholder="Jane"
                      autoComplete="given-name"
                      required
                    />
                    <TextInput
                      id="ca-last"
                      label="Last Name"
                      value={lastName}
                      onChange={(e) => { setLastName(e.target.value); clearError(); }}
                      placeholder="Smith"
                      autoComplete="family-name"
                      required
                    />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <TextInput
                      id="ca-email"
                      label="Work Email"
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); clearError(); }}
                      placeholder="jane@yourfirm.com"
                      autoComplete="email"
                      required
                    />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <TextInput
                      id="ca-company"
                      label="Firm / Company"
                      value={companyName}
                      onChange={(e) => { setCompanyName(e.target.value); clearError(); }}
                      placeholder="Acme Capital"
                      autoComplete="organization"
                      required
                    />
                  </div>

                  {/* Phone / AUM row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                    <TextInput
                      id="ca-phone"
                      label="Phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => { setPhone(e.target.value); clearError(); }}
                      placeholder="+1 212 555 0100"
                      autoComplete="tel"
                    />
                    <SelectInput
                      id="ca-aum"
                      label="AUM (approx.)"
                      value={aum}
                      onChange={(e) => setAum(e.target.value)}
                      options={AUM_OPTIONS}
                      placeholder="Select range"
                    />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <TextInput
                      id="ca-address"
                      label="Address"
                      value={address}
                      onChange={(e) => { setAddress(e.target.value); clearError(); }}
                      placeholder="123 Main St, New York, NY 10001"
                      autoComplete="street-address"
                    />
                  </div>

                  <div style={{ marginBottom: '1.25rem' }}>
                    <SelectInput
                      id="ca-interest"
                      label="Primary Interest"
                      value={primaryInterest}
                      onChange={(e) => setPrimaryInterest(e.target.value)}
                      options={INTEREST_OPTIONS}
                      placeholder="Select an option"
                    />
                  </div>

                  <p style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.6, marginBottom: '0.9rem' }}>
                    Fields marked <span style={{ color: '#C9A84C' }}>*</span> are required. We'll review your application and send you an activation email within one business day.
                  </p>

                  <button
                    type="submit"
                    disabled={loading}
                    style={{ ...btnSubmitStyle, opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                  >
                    {loading ? 'Submitting...' : 'Create Account'}
                    {!loading && <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 7h10M8 3l4 4-4 4" /></svg>}
                  </button>
                </form>

                <div style={{ width: '100%', height: '1px', background: 'rgba(255,255,255,0.08)', margin: '1.5rem 0 1.1rem' }} />
                <div style={{ fontFamily: 'var(--font-cond)', fontSize: '0.72rem', letterSpacing: '0.04em', color: 'rgba(255,255,255,0.28)', textAlign: 'center', lineHeight: 1.7 }}>
                  Already have an account?{' '}
                  <a href="/login" style={{ color: 'rgba(201,168,76,0.6)', textDecoration: 'none' }}>Sign in</a>
                  {' '}or{' '}
                  <a href="#" onClick={(e) => { e.preventDefault(); setDemoOpen(true); }} style={{ color: 'rgba(201,168,76,0.6)', textDecoration: 'none' }}>request a demo</a>
                  {' '}instead.<br />
                  Need help?{' '}
                  <a href="mailto:info@tailriskglobal.com" style={{ color: 'rgba(201,168,76,0.6)', textDecoration: 'none' }}>info@tailriskglobal.com</a>
                </div>
              </>
            ) : (
              /* Success state */
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: '56px', height: '56px', background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.4rem' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                </div>
                <div style={{ width: '28px', height: '2px', background: '#C9A84C', opacity: 0.5, margin: '0 auto 1.2rem' }} />
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 600, color: '#fff', marginBottom: '0.65rem' }}>
                  Application received
                </div>
                <p style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.75, marginBottom: '1.5rem' }}>
                  Thank you for registering with Tail Risk Global.<br />
                  We'll review your application and send you an activation email within one business day.
                </p>
                <div style={{ width: '100%', height: '1px', background: 'rgba(255,255,255,0.08)', margin: '1.5rem 0 1.1rem' }} />
                <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', textAlign: 'center' }}>
                  <a href="/" style={{ color: '#d4b06a', textDecoration: 'none' }}>← Return to home</a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <DemoModal
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        onSignIn={() => navigate('/login')}
        initialFirst={firstName}
        initialLast={lastName}
        initialEmail={email}
        initialCompany={companyName}
      />
    </>
  );
}
